<template>
  <b-modal
    hide-footer
    size="xl"
    title="League Name Mapping"
    centered
    id="league-mapping"
    @shown="handleGetListSport"
    @hide="handleModalHide"
  >
    <div class="text-black pt-2 pb-2">
      <b-row>
        <!-- LIST SPORT -->
        <b-col cols="12" lg="4" style="border-right: 1px solid #dae1e7">
          <div
            v-for="(sport, index) in listSports"
            :key="sport.id"
            class="d-flex align-items-center justify-content-between p-1"
            :class="{
              'border-bot': index !== listSports.length - 1,
              'active-item': selectedSport && selectedSport.id === sport.id,
            }"
            style="cursor: pointer; gap: 16px"
            @click="handleSelectSport(sport)"
          >
            <div>{{ sport.name }}</div>
          </div>
        </b-col>

        <!-- LEAGUE NAME MAPPING -->
        <b-col v-if="selectedSport" cols="12" lg="8">
          <div class="d-flex justify-content-between mb-1">
            <h5>LEAGUE NAME MAPPING</h5>
            <b-button
              variant="primary"
              style="padding: 6px 10px; font-size: 12px"
              @click="handleShowAddLeagueMap"
              >Add mapping</b-button
            >
          </div>

          <b-form-input
            v-model="leagueMapSearch"
            class="border mb-1"
            placeholder="Search by league name..."
            @input="handleSearchLeagueMap"
          />

          <div v-if="leagueMapList.length > 0">
            <div
              v-for="item in leagueMapList"
              :key="item.sportCastLeagueMapId"
              class="d-flex align-items-center justify-content-between p-1 border-bot"
            >
              <div>
                <div style="font-weight: 600; font-size: 13px">{{ item.alias }}</div>
                <div class="text-muted" style="font-size: 12px">{{ item.canonicalLeague }}</div>
              </div>
              <div class="d-flex" style="gap: 6px">
                <feather-icon
                  size="18"
                  icon="TrashIcon"
                  class="text-danger cursor-pointer"
                  @click="showDeleteLeagueMap(item)"
                />
                <feather-icon
                  size="18"
                  icon="EditIcon"
                  class="text-warning cursor-pointer"
                  @click="handleShowEditLeagueMap(item)"
                />
              </div>
            </div>
            <div v-if="leagueMapHasMore" class="text-center mt-1">
              <b-button
                variant="outline-primary"
                size="sm"
                :disabled="leagueMapLoading"
                @click="handleLoadMoreLeagueMap"
              >
                <Spinner v-if="leagueMapLoading" />
                <span v-else>Load more</span>
              </b-button>
            </div>
          </div>
          <div v-else class="mt-3 text-center">
            <Spinner v-if="leagueMapLoading" />
            <span v-else>No items in list</span>
          </div>
        </b-col>
      </b-row>

      <!-- MODAL ADD/EDIT LEAGUE MAPPING -->
      <b-modal
        ref="league-map-modal"
        :title="leagueMapModalType === 'add' ? 'Add League Mapping' : 'Edit League Mapping'"
        centered
        cancel-variant="outline-secondary"
        hide-footer
        @hide="hideLeagueMapModal"
      >
        <div style="color: black" class="mb-2">
          <p class="m-0" style="padding-bottom: 5px">Sportcast League Name</p>
          <b-form-input
            v-model="leagueMapForm.canonicalLeague"
            class="border"
            placeholder="Enter league name..."
          />
        </div>
        <div style="color: black" class="mb-2">
          <p class="m-0" style="padding-bottom: 5px">Sporting Solutions League Name</p>
          <b-form-input
            v-model="leagueMapForm.alias"
            class="border"
            placeholder="Enter league name..."
          />
        </div>
        <b-row>
          <b-col class="d-flex justify-content-end mt-2">
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0"
              :disabled="leagueMapSaving"
              @click="handleSaveLeagueMap"
            >
              <Spinner v-if="leagueMapSaving" />
              <template v-else>Save</template>
            </b-button>
          </b-col>
        </b-row>
      </b-modal>

      <!-- MODAL CONFIRM -->
      <b-modal
        ref="modal"
        :title="modal.title"
        centered
        cancel-variant="outline-secondary"
        :ok-disabled="leagueMapSaving"
        @ok="handleSubmitModal"
      >
        <b-card-text style="color: black">
          {{ modal.content }}
        </b-card-text>
        <template #modal-ok>
          <Spinner v-if="leagueMapSaving" />
          <span v-else>Submit</span>
        </template>
      </b-modal>
    </div>
  </b-modal>
</template>

<script>
import useToastHandler from "@/services/toastHandler";
import {
  BCol,
  BRow,
  BButton,
  BFormInput,
  BCardText,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import Spinner from "@/@core/components/Spinner.vue";

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BFormInput,
    BCardText,
    Spinner,
  },
  data() {
    return {
      modal: {
        title: "",
        content: "",
      },
      action: {
        type: "",
        item: null,
      },
      listSports: [],
      selectedSport: null,
      // League map
      leagueMapList: [],
      leagueMapPage: 1,
      leagueMapSearch: "",
      leagueMapHasMore: false,
      leagueMapLoading: false,
      leagueMapSaving: false,
      leagueMapModalType: "add",
      leagueMapForm: { canonicalLeague: "", alias: "" },
      leagueMapEditItem: null,
      searchTimeout: null,
    };
  },
  watch: {
    selectedSport(newVal) {
      if (newVal) {
        this.resetAndFetchLeagueMap();
      }
    },
  },
  methods: {
    ...mapActions({
      getListSport: "sport/list/getListSport",
      getLeagueMapList: "sport/market/getLeagueMapList",
      createLeagueMap: "sport/market/createLeagueMap",
      updateLeagueMap: "sport/market/updateLeagueMap",
      deleteLeagueMap: "sport/market/deleteLeagueMap",
    }),
    async handleGetListSport() {
      const { data } = await this.getListSport({
        page: 1,
        limit: 100,
        source: "sporting",
      });
      if (Array.isArray(data)) {
        this.listSports = data;
        this.selectedSport = this.listSports[0];
      }
    },
    handleSelectSport(sport) {
      this.selectedSport = sport;
    },
    handleModalHide() {
      this.leagueMapSearch = "";
      this.leagueMapList = [];
      this.leagueMapPage = 1;
      this.leagueMapHasMore = false;
    },
    setModal(title, content) {
      this.modal.title = title;
      this.modal.content = content;
    },
    async handleSubmitModal(event) {
      event.preventDefault();
      const { type, item } = this.action;
      if (type === "delete-league-map") await this.handleDeleteLeagueMap(item);

      if (!this.leagueMapSaving) {
        this.$nextTick(() => {
          this.$refs.modal.hide();
        });
      }
    },
    // League map
    resetAndFetchLeagueMap() {
      this.leagueMapList = [];
      this.leagueMapPage = 1;
      this.leagueMapHasMore = false;
      this.fetchLeagueMapListData();
    },
    async fetchLeagueMapListData(append = false) {
      if (!this.selectedSport) return;
      this.leagueMapLoading = true;
      try {
        const data = await this.getLeagueMapList({
          page: this.leagueMapPage,
          limit: 20,
          sportCode: this.selectedSport.name,
          ...(this.leagueMapSearch && { search: this.leagueMapSearch }),
        });
        if (data && Array.isArray(data.data)) {
          this.leagueMapList = append
            ? [...this.leagueMapList, ...data.data]
            : data.data;
          const total =
            data.total ?? data.meta?.total ?? data.pagination?.total;
          this.leagueMapHasMore =
            total !== undefined
              ? this.leagueMapList.length < total
              : data.data.length === 20;
        }
      } finally {
        this.leagueMapLoading = false;
      }
    },
    handleLoadMoreLeagueMap() {
      this.leagueMapPage += 1;
      this.fetchLeagueMapListData(true);
    },
    handleSearchLeagueMap() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.leagueMapPage = 1;
        this.leagueMapList = [];
        this.fetchLeagueMapListData();
      }, 400);
    },
    handleShowAddLeagueMap() {
      this.leagueMapModalType = "add";
      this.leagueMapForm = { canonicalLeague: "", alias: "" };
      this.leagueMapEditItem = null;
      this.$refs["league-map-modal"].show();
    },
    handleShowEditLeagueMap(item) {
      this.leagueMapModalType = "edit";
      this.leagueMapForm = {
        canonicalLeague: item.canonicalLeague,
        alias: item.alias,
      };
      this.leagueMapEditItem = item;
      this.$refs["league-map-modal"].show();
    },
    hideLeagueMapModal() {
      this.leagueMapForm = { canonicalLeague: "", alias: "" };
      this.leagueMapEditItem = null;
    },
    async handleSaveLeagueMap() {
      if (!this.leagueMapForm.canonicalLeague)
        return this.showToastError(false, "Please enter Sportcast League Name");
      if (!this.leagueMapForm.alias)
        return this.showToastError(false, "Please enter Sporting Solutions League Name");

      this.leagueMapSaving = true;
      try {
        if (this.leagueMapModalType === "add") {
          const res = await this.createLeagueMap({
            sportCode: this.selectedSport.name,
            canonicalLeague: this.leagueMapForm.canonicalLeague,
            alias: this.leagueMapForm.alias,
          });
          if (res) {
            this.showToastSuccess(true, "League mapping added successfully!");
            this.$refs["league-map-modal"].hide();
            this.resetAndFetchLeagueMap();
          }
        } else {
          const res = await this.updateLeagueMap({
            id: this.leagueMapEditItem.sportCastLeagueMapId,
            canonicalLeague: this.leagueMapForm.canonicalLeague,
            alias: this.leagueMapForm.alias,
          });
          if (res) {
            this.showToastSuccess(true, "League mapping updated successfully!");
            this.$refs["league-map-modal"].hide();
            this.resetAndFetchLeagueMap();
          }
        }
      } finally {
        this.leagueMapSaving = false;
      }
    },
    showDeleteLeagueMap(item) {
      this.action = { item, type: "delete-league-map" };
      this.setModal(
        "Delete League Mapping",
        `Are you sure you want to delete mapping "${item.canonicalLeague}"?`
      );
      this.$refs.modal.show();
    },
    async handleDeleteLeagueMap(item) {
      this.leagueMapSaving = true;
      try {
        const res = await this.deleteLeagueMap(item.sportCastLeagueMapId);
        if (res) {
          this.showToastSuccess(true, "League mapping deleted successfully!");
          this.resetAndFetchLeagueMap();
        }
      } finally {
        this.leagueMapSaving = false;
      }
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return {
      showToastError,
      showToastSuccess,
    };
  },
};
</script>

<style lang="scss" scoped>
.border-bot {
  border-bottom: 1px solid #dae1e7;
}

.active-item {
  color: #0d6efd;
  font-weight: bold;
  border-left: 2px solid #0d6efd;
  background: #e7f1ff;
}
</style>
